/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { UploadFile } from "@duxton-web/ui"

import { getHelpCentreUrl } from "constants/domain"

/**
 * Canonical Return/Refund status taxonomy.
 *
 * Values are placeholders until the backend enum lands (spec pending — see Q15/Q31 in the
 * story breakdown). Name them after the BE enum when it arrives so nothing downstream
 * has to be renamed once `mexcoreapi-sellercenter.type.ts` is regenerated.
 */
export const RETURN_STATUS = {
  acknowledgingRequest: "ACKNOWLEDGING_REQUEST",
  verifyingReturnedItem: "VERIFYING_RETURNED_ITEM",
  // Per-item only, and absent from the design's status legend — it appears on the
  // in-transit frame as `Return Requested`. Reconcile against the BE enum (Q15).
  returnRequested: "RETURN_REQUESTED",
  deliveringReturnedItem: "DELIVERING_RETURNED_ITEM",
  requestCanceled: "REQUEST_CANCELED",
  refunded: "REFUNDED",
  rejected: "REJECTED",
} as const

export type ReturnStatus = typeof RETURN_STATUS[keyof typeof RETURN_STATUS]

/**
 * The backend's real per-item lifecycle status, mirroring `ItemStatus` in
 * `food/common/constant/returnrefund/item_status.go`. This is what the worklist's
 * `requestStatus` carries and what its `statusFilter` is validated against — the server
 * allowlists exactly these values and rejects the whole request with invalid-argument
 * otherwise.
 *
 * Distinct from `RETURN_STATUS` above, which is the FE placeholder taxonomy that predates
 * the contract and matches no backend value. `RETURN_STATUS` still drives the drawer's own
 * detail representation (per-item status via `RETURN_WORKLIST_STATUS_TO_DETAIL_STATUS`);
 * its retirement is separate, larger work.
 */
export const RETURN_WORKLIST_STATUS = {
  pendingMexReview: "PENDING_MEX_REVIEW",
  infoRequested: "INFO_REQUESTED",
  pendingGsReviewPreReturn: "PENDING_GS_REVIEW_PRE_RETURN",
  pendingGsReviewPostReturn: "PENDING_GS_REVIEW_POST_RETURN",
  awaitingBooking: "AWAITING_BOOKING",
  returnInProgress: "RETURN_IN_PROGRESS",
  returnedPendingInspection: "RETURNED_PENDING_INSPECTION",
  refundProcessing: "REFUND_PROCESSING",
  refunded: "REFUNDED",
  refundFailed: "REFUND_FAILED",
  rejectedByMex: "REJECTED_BY_MEX",
  rejectedByGs: "REJECTED_BY_GS",
  cancelled: "CANCELLED",
  expired: "EXPIRED",
} as const

export type ReturnWorklistStatus = typeof RETURN_WORKLIST_STATUS[keyof typeof RETURN_WORKLIST_STATUS]

/**
 * Statuses offered in the Return status column filter, in display order.
 *
 * A deliberate subset of what the backend accepts: `UNSPECIFIED` is not a real state, and
 * `PENDING_GS_REVIEW` / `REJECTED` are retained by the backend only for reading legacy
 * persisted records, so neither belongs in a merchant-facing picker.
 */
export const RETURN_WORKLIST_STATUS_FILTER_OPTIONS: ReturnWorklistStatus[] = [
  RETURN_WORKLIST_STATUS.pendingMexReview,
  RETURN_WORKLIST_STATUS.infoRequested,
  RETURN_WORKLIST_STATUS.awaitingBooking,
  RETURN_WORKLIST_STATUS.returnInProgress,
  RETURN_WORKLIST_STATUS.returnedPendingInspection,
  RETURN_WORKLIST_STATUS.pendingGsReviewPreReturn,
  RETURN_WORKLIST_STATUS.pendingGsReviewPostReturn,
  RETURN_WORKLIST_STATUS.refundProcessing,
  RETURN_WORKLIST_STATUS.refunded,
  RETURN_WORKLIST_STATUS.refundFailed,
  RETURN_WORKLIST_STATUS.rejectedByMex,
  RETURN_WORKLIST_STATUS.rejectedByGs,
  RETURN_WORKLIST_STATUS.cancelled,
  RETURN_WORKLIST_STATUS.expired,
]

/**
 * Coarse status groups the worklist's `statusFilter` accepts on the Feedback tab's picker,
 * replacing the fine-grained `ItemStatus` values there — a merchant filters by where a
 * request stands, not by which of fourteen lifecycle states it is in.
 */
export const RETURN_WORKLIST_STATUS_GROUP = {
  active: "ACTIVE",
  resolved: "RESOLVED",
  cancelled: "CANCELLED",
} as const

export type ReturnWorklistStatusGroup = typeof RETURN_WORKLIST_STATUS_GROUP[keyof typeof RETURN_WORKLIST_STATUS_GROUP]

/** Groups offered in the Feedback tab's status filter, in display order. */
export const RETURN_WORKLIST_STATUS_GROUP_FILTER_OPTIONS: ReturnWorklistStatusGroup[] = [
  RETURN_WORKLIST_STATUS_GROUP.active,
  RETURN_WORKLIST_STATUS_GROUP.resolved,
  RETURN_WORKLIST_STATUS_GROUP.cancelled,
]

/**
 * Backend item status → the FE detail taxonomy the drawer still runs on.
 *
 * The mapping is SEMANTIC, not nominal: what matters downstream is which bucket
 * (`RETURN_STATUS_TO_BUCKET`) and progression rank each backend state lands on, because
 * those drive the footer (review vs inspection vs closed) and the least-progressed
 * header rule. Display copy does not ride on this map — the header prefers the server's
 * `requestStatusContent` whenever it is populated.
 *
 * Lossy on purpose (several backend states share an FE value); it retires together with
 * `RETURN_STATUS` once the drawer's own taxonomy is replaced wholesale — doing that
 * replacement now would drag the review-flow and mock surfaces into this slice.
 */
export const RETURN_WORKLIST_STATUS_TO_DETAIL_STATUS: Record<
  ReturnWorklistStatus,
  ReturnStatus
> = {
  // Awaiting the merchant's decision 1 — INFO_REQUESTED is still that decision,
  // merely paused on the buyer's clarification.
  [RETURN_WORKLIST_STATUS.pendingMexReview]: RETURN_STATUS.acknowledgingRequest,
  [RETURN_WORKLIST_STATUS.infoRequested]: RETURN_STATUS.acknowledgingRequest,
  // With Grab or the buyer — in progress, nothing for the merchant to do.
  [RETURN_WORKLIST_STATUS.pendingGsReviewPreReturn]:
    RETURN_STATUS.returnRequested,
  [RETURN_WORKLIST_STATUS.awaitingBooking]: RETURN_STATUS.returnRequested,
  [RETURN_WORKLIST_STATUS.pendingGsReviewPostReturn]:
    RETURN_STATUS.deliveringReturnedItem,
  [RETURN_WORKLIST_STATUS.returnInProgress]:
    RETURN_STATUS.deliveringReturnedItem,
  [RETURN_WORKLIST_STATUS.refundProcessing]:
    RETURN_STATUS.deliveringReturnedItem,
  // The merchant's decision 2.
  [RETURN_WORKLIST_STATUS.returnedPendingInspection]:
    RETURN_STATUS.verifyingReturnedItem,
  // Terminal.
  [RETURN_WORKLIST_STATUS.refunded]: RETURN_STATUS.refunded,
  [RETURN_WORKLIST_STATUS.rejectedByMex]: RETURN_STATUS.rejected,
  [RETURN_WORKLIST_STATUS.rejectedByGs]: RETURN_STATUS.rejected,
  // Closed without a refund. `REFUND_FAILED` lands here for its bucket (closed), not
  // its copy — the label is overridden by `requestStatusContent` when supplied.
  [RETURN_WORKLIST_STATUS.refundFailed]: RETURN_STATUS.requestCanceled,
  [RETURN_WORKLIST_STATUS.cancelled]: RETURN_STATUS.requestCanceled,
  [RETURN_WORKLIST_STATUS.expired]: RETURN_STATUS.requestCanceled,
}

/**
 * Statuses the backend retains only for reading legacy persisted records — accepted on
 * the way in, never offered as filters. Kept off `RETURN_WORKLIST_STATUS` so they cannot
 * be sent to the API, whose filter allowlist rejects nothing-else-but the modern set.
 */
export const LEGACY_WORKLIST_STATUS_TO_DETAIL_STATUS: Record<
  string,
  ReturnStatus
> = {
  PENDING_GS_REVIEW: RETURN_STATUS.returnRequested,
  REJECTED: RETURN_STATUS.rejected,
}

/** The three buckets from the design's own status legend. */
export const RETURN_STATUS_BUCKET = {
  actionRequired: "ACTION_REQUIRED",
  actionNotRequired: "ACTION_NOT_REQUIRED",
  closed: "CLOSED",
} as const

export type ReturnStatusBucket = typeof RETURN_STATUS_BUCKET[keyof typeof RETURN_STATUS_BUCKET]

export const RETURN_STATUS_TO_BUCKET: Record<
  ReturnStatus,
  ReturnStatusBucket
> = {
  [RETURN_STATUS.acknowledgingRequest]: RETURN_STATUS_BUCKET.actionRequired,
  [RETURN_STATUS.verifyingReturnedItem]: RETURN_STATUS_BUCKET.actionRequired,
  [RETURN_STATUS.returnRequested]: RETURN_STATUS_BUCKET.actionNotRequired,
  [RETURN_STATUS.deliveringReturnedItem]:
    RETURN_STATUS_BUCKET.actionNotRequired,
  [RETURN_STATUS.requestCanceled]: RETURN_STATUS_BUCKET.closed,
  [RETURN_STATUS.refunded]: RETURN_STATUS_BUCKET.closed,
  [RETURN_STATUS.rejected]: RETURN_STATUS_BUCKET.closed,
}

/** Sub-chip filters above the table. `Action Required` is the default selection, not `All`. */
export const RETURN_REFUND_FILTER = {
  all: "ALL",
  actionRequired: "ACTION_REQUIRED",
  ongoing: "ONGOING",
} as const

export type ReturnRefundFilter = typeof RETURN_REFUND_FILTER[keyof typeof RETURN_REFUND_FILTER]

/** `Action Required` opens first, per the design — it is the merchant-action queue. */
export const DEFAULT_RETURN_REFUND_FILTER = RETURN_REFUND_FILTER.actionRequired

/** One row in the Orders Return/Refund table = one return request. */
export interface ReturnRequestRow {
  /**
   * Identity of the return request itself. One order can carry more than one
   * request (partial return, re-request after rejection), so rows key on this,
   * not on `orderID` — that stays a display field.
   */
  requestID: string
  /** The order's real identity — what the review-detail endpoint is keyed by. */
  orderID: string
  /** Short order number shown in the ORDER ID column, e.g. `GS-125`. Display only. */
  shortOrderNumber: string
  /** Number of items flagged in the request. */
  itemCount: number
  /** Pre-formatted request timestamp, e.g. `08 Mar 11:00`. */
  requestedAt: string
  /**
   * The backend's per-request status, used by the Return status column filter.
   */
  requestStatus?: string
  /**
   * Server-rendered display text for `requestStatus`, e.g. `Pending acknowledgement`.
   * Preferred over any FE label map so a status the backend adds renders without a
   * frontend change.
   */
  statusContent?: string
  /** Server-computed bucket, e.g. `ACTION_REQUIRED`. */
  bucket?: string
  /**
   * Deadline for the merchant's next action, epoch milliseconds. The countdown is
   * derived from this at render time so it ticks, rather than being frozen at fetch.
   */
  nextActionDueAt?: number
}

/**
 * Every Return/Refund string, resolved. Each consuming component calls
 * `useTranslationsMap` directly (this repo's own convention — no shared hook)
 * with just the subset of these keys it needs, keyed to PhraseApp under
 * `ecommerce.sellerCentre.returns.*` (a few generic entries — confirm/cancel/
 * status/etc. — resolve through existing `common.*` keys instead).
 *
 * The label maps below take the caller's resolved text as a plain
 * `Record<string, string>` parameter, not `Pick<ReturnRefundText, ...>` —
 * `useTranslationsMap`'s real return type is an index signature
 * (`{[key: string]: string}`), which doesn't preserve the literal keys passed
 * in, so a `Pick<>` annotation there wouldn't actually be checked. `Pick<>` is
 * still used where a map's VALUE is itself a function (e.g.
 * `getReturnTimelineCountLabel`), since that shape does need real typing.
 */
export interface ReturnRefundText {
  tabTitle: string
  filterAll: string
  filterActionRequired: string
  filterOngoing: string
  columnOrderId: string
  columnItems: string
  columnRequested: string
  columnReturnStatus: string
  columnAction: string
  actionReview: string
  slaMinLeft: string
  emptyState: string
  errorState: string
  errorRetry: string
  // Feedback tab's item-level worklist. Its columns are per claimed item, so they are
  // deliberately separate from the Orders tab's request-level column set above.
  columnDate: string
  columnShortOrderNumber: string
  columnItemName: string
  columnQuantity: string
  columnAmount: string
  columnStatus: string
  filterStatusPlaceholder: string
  // Status-group filter option labels.
  filterStatusActive: string
  filterStatusResolved: string
  filterStatusCancelled: string
  // Status-filter option labels. The three marked `confirmed` are the server's own
  // wording, observed in `requestStatusContent`; the rest await design copy.
  worklistStatusAwaitingBooking: string // confirmed
  worklistStatusReturnInProgress: string // confirmed
  worklistStatusReturnedPendingInspection: string // confirmed
  worklistStatusInfoRequested: string
  worklistStatusPendingGsReviewPreReturn: string
  worklistStatusPendingGsReviewPostReturn: string
  worklistStatusRefundProcessing: string
  worklistStatusRefundFailed: string
  worklistStatusRejectedByGs: string
  worklistStatusExpired: string
  statusAcknowledgingRequest: string
  statusVerifyingReturnedItem: string
  statusReturnRequested: string
  statusDeliveringReturnedItem: string
  statusRefunded: string
  statusRejected: string
  statusRequestCanceled: string
  // Per-item statuses use the taxonomy legend's wording, which differs from the badge
  // wording above for the same status (`Refunded` vs `Refund approved`).
  itemStatusAcknowledgingRequest: string
  itemStatusVerifyingReturnedItem: string
  itemStatusReturnRequested: string
  itemStatusDeliveringReturnedItem: string
  itemStatusRefunded: string
  itemStatusRejected: string
  itemStatusRequestCanceled: string
  reasonItemDamaged: string
  reasonWrongItem: string
  drawerTotal: string
  drawerReturnStatusHeading: string
  drawerReviewBy: string
  drawerReviewCta: string
  // Decision 2 (inspection) reuses the same footer with its own label and CTA.
  drawerVerifyBy: string
  drawerReceivedCta: string
  drawerRaiseIssueCta: string
  drawerMinutesSuffix: string
  drawerCloseLabel: string
  drawerBackLabel: string
  drawerChatLabel: string
  drawerMoreActionsLabel: string
  detailNotFound: string
  // Shown when an action's native handler is unavailable — no bridge (desktop), no
  // handler defined, or the app refused the call. Deliberately generic: the handler
  // name is an internal detail and goes to the console instead.
  nativeActionUnavailable: string
  reviewTitle: string
  reviewItemHeading: (quantity: number, name: string) => string
  reviewReturnReason: string
  reviewBuyerMessage: string
  reviewEvidencePhotos: string
  reviewEvidencePhotoAlt: (index: number) => string
  reviewActionLabel: string
  reviewActionPlaceholder: string
  reviewStatePending: string
  reviewStateCompleted: string
  reviewConfirmCta: string
  reviewSuccess: string
  refundSuccess: string
  actionApproveReturn: string
  actionRefundNow: string
  actionRejectRequest: string
  // Inspection (decision 2). Its action set and reason list are deliberately
  // separate from the review-stage ones above.
  inspectionTitle: string
  actionApproveRefund: string
  inspectionReasonItemNotReturned: string
  inspectionReasonItemWorn: string
  inspectionReasonItemDamaged: string
  inspectionReasonItemMissingParts: string
  inspectionSuccessTitle: string
  inspectionSuccessBody: string
  inspectionSuccessDone: string
  // Placeholder until product settles the reject-outcome success copy (Q20).
  inspectionSubmitted: string
  reviewReasonLabel: string
  clarifyLabel: string
  initiateChatCta: string
  goToChatCta: string
  // The header chat entry's item picker (webview): one room per request, the
  // checked items are attached to it by the same REQUEST_INFO submit.
  chatPickerTitle: string
  chatPickerCta: string
  // The narrow-width bottom-sheet pickers (design: dropdowns become sheets below md).
  actionSheetTitle: string
  reviewReasonPlaceholder: string
  reviewSupportingDocument: string
  reviewAddPhotos: string
  /** Slot counter under the add tile's label, e.g. `(1/2)`. */
  reviewAddPhotosCount: (used: number, max: number) => string
  reviewPhotoRejected: (fileName: string) => string
  /*
   * The webview Add-photos sheet (native camera/gallery hand-off). The size in the
   * subtitle is the upload endpoint's own limit — the design mock says 45MB, but
   * stating a size the submit would then reject is worse than correcting the copy.
   * Native picks arrive compressed and sit far under it either way.
   */
  addPhotosSubtitle: (max: number) => string
  addPhotosTakePhoto: string
  addPhotosUploadPhotos: string
  addPhotoFailed: string
  photoUploadFailed: (fileName: string) => string
  reasonPerfectConditionAtDispatch: string
  reasonInconclusiveEvidence: string
  reasonMismatchedEvidence: string
  reasonContradictsDescription: string
  reasonLogisticsIssue: string
  reasonHelperPerfectConditionAtDispatch: string
  reasonHelperInconclusiveEvidence: string
  reasonHelperMismatchedEvidence: string
  reasonHelperContradictsDescription: string
  reasonHelperLogisticsIssue: string
  timelineItemDelivered: string
  timelineRequestSubmitted: string
  timelinePendingAcknowledgement: string
  timelineRequestAcknowledged: string
  timelinePendingReturn: string
  timelineReturnInTransit: string
  timelinePendingInspection: string
  timelineRefundApproved: string
  timelineRequestRejected: string
  timelineRequestCanceled: string
  timelineRejectedCount: (count: number) => string
  attributionReviewedByGrab: string
  chatUnavailableTitle: string
  chatUnavailableBody: (orderID: string) => string
  chatUnavailableCta: string
  overflowContactGrab: string
  overflowReportIssue: string
  overflowLearnMore: string
  contactGrabUnavailableTitle: string
  contactGrabUnavailableBody: string
  contactGrabUnavailableCta: string
  actionTrackHere: string
  actionContactDriver: string
  actionContactGrabSupport: string
  alertDeliveryAttemptFailed: string
  alertDriverIssue: string
}

/**
 * Backend status → filter-option label.
 *
 * Only the Return status column filter reads this: the table cell itself renders the
 * server's `statusContent`, so a status the backend adds shows correct text without a
 * frontend change. This map exists because a filter has to name its options before any
 * row is fetched.
 */
export const getReturnWorklistStatusLabel = (
  text: Record<string, string>,
): Record<ReturnWorklistStatus, string> => ({
  [RETURN_WORKLIST_STATUS.pendingMexReview]: text.statusAcknowledgingRequest,
  [RETURN_WORKLIST_STATUS.infoRequested]: text.worklistStatusInfoRequested,
  [RETURN_WORKLIST_STATUS.pendingGsReviewPreReturn]:
    text.worklistStatusPendingGsReviewPreReturn,
  [RETURN_WORKLIST_STATUS.pendingGsReviewPostReturn]:
    text.worklistStatusPendingGsReviewPostReturn,
  [RETURN_WORKLIST_STATUS.awaitingBooking]: text.worklistStatusAwaitingBooking,
  [RETURN_WORKLIST_STATUS.returnInProgress]:
    text.worklistStatusReturnInProgress,
  [RETURN_WORKLIST_STATUS.returnedPendingInspection]:
    text.worklistStatusReturnedPendingInspection,
  [RETURN_WORKLIST_STATUS.refundProcessing]:
    text.worklistStatusRefundProcessing,
  [RETURN_WORKLIST_STATUS.refunded]: text.statusRefunded,
  [RETURN_WORKLIST_STATUS.refundFailed]: text.worklistStatusRefundFailed,
  [RETURN_WORKLIST_STATUS.rejectedByMex]: text.statusRejected,
  [RETURN_WORKLIST_STATUS.rejectedByGs]: text.worklistStatusRejectedByGs,
  [RETURN_WORKLIST_STATUS.cancelled]: text.statusRequestCanceled,
  [RETURN_WORKLIST_STATUS.expired]: text.worklistStatusExpired,
})

/** Status group → filter-option label, the group counterpart of the map above. */
export const getReturnWorklistStatusGroupLabel = (
  text: Record<string, string>,
): Record<ReturnWorklistStatusGroup, string> => ({
  [RETURN_WORKLIST_STATUS_GROUP.active]: text.filterStatusActive,
  [RETURN_WORKLIST_STATUS_GROUP.resolved]: text.filterStatusResolved,
  [RETURN_WORKLIST_STATUS_GROUP.cancelled]: text.filterStatusCancelled,
})

/** Status → label. */
export const getReturnStatusLabel = (
  text: Record<string, string>,
): Record<ReturnStatus, string> => ({
  [RETURN_STATUS.acknowledgingRequest]: text.statusAcknowledgingRequest,
  [RETURN_STATUS.verifyingReturnedItem]: text.statusVerifyingReturnedItem,
  [RETURN_STATUS.returnRequested]: text.statusReturnRequested,
  [RETURN_STATUS.deliveringReturnedItem]: text.statusDeliveringReturnedItem,
  [RETURN_STATUS.refunded]: text.statusRefunded,
  [RETURN_STATUS.rejected]: text.statusRejected,
  [RETURN_STATUS.requestCanceled]: text.statusRequestCanceled,
})

/**
 * Named seam for the per-item lifecycle. Items share the request taxonomy today, but the
 * BE spec (Q31) may ship a distinct per-item enum. The alias (and the item bucket map
 * below) mark every item-status site so that divergence surfaces as compile errors in
 * this file first — the label map and mock payloads still need their own updates then.
 */
export type ReturnItemStatus = ReturnStatus

/**
 * Bucket resolution for ITEM statuses. Aliases the request map while the taxonomies are
 * identical; if a per-item enum diverges, this map is where item buckets get their own
 * definition instead of silently falling out of every actionability check.
 */
export const RETURN_ITEM_STATUS_TO_BUCKET: Record<
  ReturnItemStatus,
  ReturnStatusBucket
> = RETURN_STATUS_TO_BUCKET

/**
 * Status → per-item label. Deliberately separate from `RETURN_STATUS_LABEL`: the design
 * shows `Refunded` on an item row while the header badge for the same status reads
 * `Refund approved`.
 */
export const getReturnItemStatusLabel = (
  text: Record<string, string>,
): Record<ReturnItemStatus, string> => ({
  [RETURN_STATUS.acknowledgingRequest]: text.itemStatusAcknowledgingRequest,
  [RETURN_STATUS.verifyingReturnedItem]: text.itemStatusVerifyingReturnedItem,
  [RETURN_STATUS.returnRequested]: text.itemStatusReturnRequested,
  [RETURN_STATUS.deliveringReturnedItem]: text.itemStatusDeliveringReturnedItem,
  [RETURN_STATUS.refunded]: text.itemStatusRefunded,
  [RETURN_STATUS.rejected]: text.itemStatusRejected,
  [RETURN_STATUS.requestCanceled]: text.itemStatusRequestCanceled,
})

/** Why the buyer flagged an item. Rendered as the red tag above the item name. */
export const RETURN_REASON = {
  itemDamaged: "ITEM_DAMAGED",
  wrongItem: "WRONG_ITEM",
} as const

export type ReturnReason = typeof RETURN_REASON[keyof typeof RETURN_REASON]

export const getReturnReasonLabel = (
  text: Record<string, string>,
): Record<ReturnReason, string> => ({
  [RETURN_REASON.itemDamaged]: text.reasonItemDamaged,
  [RETURN_REASON.wrongItem]: text.reasonWrongItem,
})

/**
 * Timeline steps. The set rendered varies by outcome — a closed auto-refund shows three,
 * an open request shows five — so the drawer takes the list as data and never assumes a
 * fixed count.
 */
export const RETURN_TIMELINE_STEP = {
  itemDelivered: "ITEM_DELIVERED",
  requestSubmitted: "REQUEST_SUBMITTED",
  pendingAcknowledgement: "PENDING_ACKNOWLEDGEMENT",
  requestAcknowledged: "REQUEST_ACKNOWLEDGED",
  pendingReturn: "PENDING_RETURN",
  returnInTransit: "RETURN_IN_TRANSIT",
  pendingInspection: "PENDING_INSPECTION",
  refundApproved: "REFUND_APPROVED",
  requestRejected: "REQUEST_REJECTED",
  requestCanceled: "REQUEST_CANCELED",
} as const

export type ReturnTimelineStep = typeof RETURN_TIMELINE_STEP[keyof typeof RETURN_TIMELINE_STEP]

export const RETURN_TIMELINE_STATE = {
  complete: "COMPLETE",
  current: "CURRENT",
  upcoming: "UPCOMING",
} as const

export type ReturnTimelineState = typeof RETURN_TIMELINE_STATE[keyof typeof RETURN_TIMELINE_STATE]

/**
 * `stepState` values the review-detail endpoint sends on each timeline step. Kept
 * separate from `RETURN_TIMELINE_STATE` (the FE's own vocabulary, which predates the
 * contract) because the two disagree on the completed spelling: `DONE` vs `COMPLETE`.
 */
export const RETURN_TIMELINE_SERVER_STATE = {
  done: "DONE",
  current: "CURRENT",
  upcoming: "UPCOMING",
} as const

export const RETURN_TIMELINE_SERVER_STATE_TO_STATE: Record<
  string,
  ReturnTimelineState
> = {
  [RETURN_TIMELINE_SERVER_STATE.done]: RETURN_TIMELINE_STATE.complete,
  [RETURN_TIMELINE_SERVER_STATE.current]: RETURN_TIMELINE_STATE.current,
  [RETURN_TIMELINE_SERVER_STATE.upcoming]: RETURN_TIMELINE_STATE.upcoming,
}

/**
 * Server timeline `stepCode` → the FE step it means. Needed for BEHAVIOUR, not display
 * (the server's `title` wins on screen regardless): `applyReviewDecisions` finds the
 * acknowledgement step by FE code to advance the timeline optimistically, so a raw server
 * code would make that lookup silently miss. Codes without an FE equivalent (the GS-review
 * step, `RESOLVED`) pass through raw — no FE behaviour keys on them.
 */
export const RETURN_SERVER_STEP_TO_TIMELINE_STEP: Record<
  string,
  ReturnTimelineStep
> = {
  REQUEST_SUBMITTED: RETURN_TIMELINE_STEP.requestSubmitted,
  PENDING_MEX_REVIEW: RETURN_TIMELINE_STEP.pendingAcknowledgement,
  AWAITING_BOOKING: RETURN_TIMELINE_STEP.pendingReturn,
  RETURNED_PENDING_INSPECTION: RETURN_TIMELINE_STEP.pendingInspection,
  REFUNDED: RETURN_TIMELINE_STEP.refundApproved,
  DIRECT_REFUND_APPROVED: RETURN_TIMELINE_STEP.refundApproved,
}

/**
 * Contextual link a delivery state can attach to the drawer. The same set serves the alert
 * banner and the timeline step, because a step carrying an action link is one timeline
 * capability rather than a component per delivery state.
 */
export const RETURN_CONTEXTUAL_ACTION = {
  trackHere: "TRACK_HERE",
  contactDriver: "CONTACT_DRIVER",
  contactGrabSupport: "CONTACT_GRAB_SUPPORT",
} as const

export type ReturnContextualAction = typeof RETURN_CONTEXTUAL_ACTION[keyof typeof RETURN_CONTEXTUAL_ACTION]

/**
 * JS callbacks the native app registers for this feature. The remaining hand-offs
 * (`Contact Driver`, `Contact Grab support`, `Contact Grab`) have no handler yet, so they
 * say so rather than naming a callback that could never fire.
 *
 * `notifyNativeHandler` invokes these through the bridge. The payload each one expects is
 * assembled centrally in `buildHandlerArgs` — the shapes come from the spec's handler
 * table and are still awaiting confirmation from the app team (Q28).
 */
export const RETURN_NATIVE_HANDLER = {
  trackHere: "onClickTrackHere",
  /*
   * Dismissing the webview itself — the one exit browser history cannot perform. At the
   * request level there is nothing left to pop, so `goBack()` leaves the merchant on a
   * webview that will not close; every step ABOVE that level (review, inspect) is ordinary
   * browser history and never comes here, which is how the app team asked for it.
   *
   * Supersedes `onClickBack`, which the app never registered — the chevron simply did
   * nothing in the app until this handler.
   */
  closeWebview: "closeWebview",
  /*
   * Listed in the app's handler table but absent from its deeplink switch, so the app owns
   * what they do: a bottom sheet for `option`, the chat screen for `chat`.
   */
  option: "onClickOption",
  chat: "onClickChat",
} as const

/**
 * App deeplinks behind the header's overflow actions in a webview, routed by the app's
 * URL interception (the same mechanism the timeline's `grabmex://EXPRESS` links use)
 * rather than a registered bridge handler. The host is the app's to resolve — the
 * deeplink carries only the path.
 *
 * `helpCentre` opens the Return and Refund article, shared by `Report an issue` and
 * `Learn about Return and Refund` deliberately.
 */
const RETURN_HELP_CENTRE_PATH = "merchant/40002522"

export const RETURN_OVERFLOW_DEEPLINK = {
  contactGrab: "grabmex://contact_grab",
  helpCentre: `grabmex://helpcenter?path=${RETURN_HELP_CENTRE_PATH}`,
} as const

/**
 * Desktop destination for the same two Help Centre rows: the Return and Refund article.
 * The host follows the portal's environment (the staging portal must not send merchants
 * to the production Help Centre); the locale-less path lets the Help Centre pick the
 * locale.
 */
export const getReturnHelpCentreUrl = (): string =>
  getHelpCentreUrl(RETURN_HELP_CENTRE_PATH)

/** Where a chat-unavailable popup was opened from — currently only the request drawer. */
export const RETURN_REFUND_CHAT_SOURCE_STATE = {
  details: "RETURN_REFUND_DETAILS",
} as const

export type ReturnRefundChatSourceState = typeof RETURN_REFUND_CHAT_SOURCE_STATE[keyof typeof RETURN_REFUND_CHAT_SOURCE_STATE]

/** Which native callback each contextual link maps to. Absent = none defined yet. */
export const RETURN_CONTEXTUAL_ACTION_HANDLER: Partial<Record<
  ReturnContextualAction,
  string
>> = {
  [RETURN_CONTEXTUAL_ACTION.trackHere]: RETURN_NATIVE_HANDLER.trackHere,
}

export const getReturnContextualActionLabel = (
  text: Record<string, string>,
): Record<ReturnContextualAction, string> => ({
  [RETURN_CONTEXTUAL_ACTION.trackHere]: text.actionTrackHere,
  [RETURN_CONTEXTUAL_ACTION.contactDriver]: text.actionContactDriver,
  [RETURN_CONTEXTUAL_ACTION.contactGrabSupport]: text.actionContactGrabSupport,
})

/** Delivery problems surfaced as a red banner under the drawer title. */
export const RETURN_ALERT = {
  deliveryAttemptFailed: "DELIVERY_ATTEMPT_FAILED",
  driverIssue: "DRIVER_ISSUE",
} as const

export type ReturnAlert = typeof RETURN_ALERT[keyof typeof RETURN_ALERT]

export interface ReturnRequestAlert {
  kind: ReturnAlert
  action?: ReturnContextualAction
}

export const getReturnAlertMessage = (
  text: Record<string, string>,
): Record<ReturnAlert, string> => ({
  [RETURN_ALERT.deliveryAttemptFailed]: text.alertDeliveryAttemptFailed,
  [RETURN_ALERT.driverIssue]: text.alertDriverIssue,
})

/**
 * Who decided a step, when it wasn't the merchant. This is the only thing separating an
 * automated refund's timeline from a merchant-initiated one — both end at `Refund approved`.
 */
export const RETURN_TIMELINE_ATTRIBUTION = {
  reviewedByGrab: "REVIEWED_BY_GRAB",
} as const

export type ReturnTimelineAttribution = typeof RETURN_TIMELINE_ATTRIBUTION[keyof typeof RETURN_TIMELINE_ATTRIBUTION]

export const getReturnTimelineAttributionLabel = (
  text: Record<string, string>,
): Record<ReturnTimelineAttribution, string> => ({
  [RETURN_TIMELINE_ATTRIBUTION.reviewedByGrab]: text.attributionReviewedByGrab,
})

export interface ReturnTimelineEntry {
  step: ReturnTimelineStep
  state: ReturnTimelineState
  /**
   * Server-rendered step title. The review-detail endpoint owns the timeline copy
   * (the contract forbids deriving it client-side), so when this is present it wins
   * over the FE's `RETURN_TIMELINE_LABEL` lookup — which stays as the fallback for
   * mock-backed steps and for a response that arrives without titles.
   */
  title?: string
  /**
   * Timestamp on completed steps, guidance copy on the ones still ahead.
   * The guidance strings are FE-owned copy — when the PhraseApp swap happens they
   * must join it (they currently ride in mock data and would be easy to miss).
   */
  detail?: string
  /** Renders a link below the detail, e.g. `Contact Driver` on `Pending return`. */
  action?: ReturnContextualAction
  /** Server-rendered copy for `action`; wins over the FE label map when present. */
  actionLabel?: string
  /**
   * The action's server-supplied destination, verbatim (`value` on the wire — the
   * consumer app's `grab://open?screenType=EXPRESS_WEB_TRACKING&page=…` deeplink for
   * `Track here`; empty for `Contact Grab support` until BE fills its TODO). RAW, not
   * openable as-is: `ReturnStatusTimeline` resolves it per host — the extracted https
   * page in a new tab on the web, a `grabmex://` re-wrap in the app webview — and
   * only unresolvable values fall back to the `onAction` hand-off.
   */
  actionHref?: string
  /** Item tally on steps that carry one, e.g. `2 Rejected` on `Request rejected`. */
  count?: number
  /** Rendered when the step was decided by someone other than the merchant. */
  attribution?: ReturnTimelineAttribution
}

export const getReturnTimelineLabel = (
  text: Record<string, string>,
): Record<ReturnTimelineStep, string> => ({
  [RETURN_TIMELINE_STEP.itemDelivered]: text.timelineItemDelivered,
  [RETURN_TIMELINE_STEP.requestSubmitted]: text.timelineRequestSubmitted,
  [RETURN_TIMELINE_STEP.pendingAcknowledgement]:
    text.timelinePendingAcknowledgement,
  [RETURN_TIMELINE_STEP.requestAcknowledged]: text.timelineRequestAcknowledged,
  [RETURN_TIMELINE_STEP.pendingReturn]: text.timelinePendingReturn,
  [RETURN_TIMELINE_STEP.returnInTransit]: text.timelineReturnInTransit,
  [RETURN_TIMELINE_STEP.pendingInspection]: text.timelinePendingInspection,
  [RETURN_TIMELINE_STEP.refundApproved]: text.timelineRefundApproved,
  [RETURN_TIMELINE_STEP.requestRejected]: text.timelineRequestRejected,
  [RETURN_TIMELINE_STEP.requestCanceled]: text.timelineRequestCanceled,
})

/**
 * How a step's `count` reads. Keyed by step so a tally on another step is a data change
 * rather than a branch in the timeline component. Steps absent here ignore `count`.
 */
export const getReturnTimelineCountLabel = (
  text: Pick<ReturnRefundText, "timelineRejectedCount">,
): Partial<Record<ReturnTimelineStep, (count: number) => string>> => ({
  [RETURN_TIMELINE_STEP.requestRejected]: text.timelineRejectedCount,
})

/** One flagged line in the request. */
export interface ReturnRequestItem {
  id: string
  /**
   * The wire identity pair, kept raw so the decision submit never has to parse it
   * back out of the composite `id`. Absent on mock-backed items, which never reach
   * the real submit.
   */
  itemID?: string
  displayModifierGroup?: string
  name: string
  /** Variant descriptor, e.g. `USB-C, White`. */
  variant?: string
  /** Per-item status — items progress independently, so a request can be mixed-state. */
  status?: ReturnItemStatus
  /** Absent when the buyer did not flag this item. */
  reason?: ReturnReason
  /**
   * Display copy for `reason`, sourced from the server (`reasonDisplayName`, or the
   * reason code prettified while that field is unpopulated). Wins over the FE
   * `RETURN_REASON_LABEL` lookup, whose keys only ever matched the mock taxonomy.
   */
  reasonLabel?: string
  quantity: number
  /**
   * Decision actions the server allows for this item, mapped from
   * `eligibleResolutions`. `undefined` = the server omitted the field (unpopulated
   * BE-side today) and the FE falls back to the full action set; `[]` = the server
   * explicitly allows none, so the item is not decidable.
   */
  eligibleActions?: DecisionAction[]
  /** Pre-formatted price, e.g. `$129.00`. */
  price: string
  imageURL?: string
  /**
   * The once-per-item formal information request has been spent — a second
   * REQUEST_INFO decision for this item is rejected server-side.
   */
  infoRequestUsed?: boolean
  /**
   * Message Center room this item is attached to, once a clarify decision has
   * opened one. One room per REQUEST — items clarified in the same request all
   * carry the same room — so this marks "already discussable" and gives the
   * Go-to-chat hand-off its destination.
   */
  roomID?: string
  /**
   * Whether the server currently lists REQUEST_INFO in the item's
   * `eligibleResolutions`. Absent (mock items, an unpopulated wire field) means
   * "not stated" and the FE stays permissive — only an explicit false hides the
   * clarify row, since submitting outside the eligibility list is refused.
   */
  canRequestInfo?: boolean
  /** Buyer's own words, quoted on the review screen. */
  buyerMessage?: string
  /** Buyer's uploaded evidence. Empty or absent renders no gallery. */
  evidencePhotos?: string[]
  /**
   * MERCHANT evidence already staged server-side (the detail's `draftMedia`) —
   * distinct from the buyer's immutable `evidencePhotos`. The upload forms render
   * from this list: it is the record of what a REJECT decision will attach.
   */
  draftPhotos?: ReturnDraftPhoto[]
}

/** One server-staged merchant photo — the identity the replace/delete flows key on. */
export interface ReturnDraftPhoto {
  assetID: string
  url: string
  fileName?: string
}

/**
 * Review (decision 1) outcomes. Inspection (decision 2) brings its own action set
 * with BP-54659/60 — the decision-point switch arrives with it rather than as an
 * empty placeholder now.
 */
export const DECISION_ACTION = {
  approveReturn: "APPROVE_RETURN",
  refundNow: "REFUND_NOW",
  rejectRequest: "REJECT_REQUEST",
} as const

export type DecisionAction = typeof DECISION_ACTION[keyof typeof DECISION_ACTION]

export const getDecisionActionLabel = (
  text: Record<string, string>,
): Record<DecisionAction, string> => ({
  [DECISION_ACTION.approveReturn]: text.actionApproveReturn,
  [DECISION_ACTION.refundNow]: text.actionRefundNow,
  [DECISION_ACTION.rejectRequest]: text.actionRejectRequest,
})

/**
 * Wire `MexReturnRefundResolution` values -> the review screen's decision actions.
 * `REQUEST_INFO` is deliberately unmapped: it is the chat flow (BP-54655), not a
 * card action, so its presence alone must not make an item reviewable.
 */
export const RETURN_RESOLUTION_TO_DECISION_ACTION: Record<
  string,
  DecisionAction
> = {
  MEX_RETURN_REFUND_RESOLUTION_APPROVE_RETURN_AND_REFUND:
    DECISION_ACTION.approveReturn,
  MEX_RETURN_REFUND_RESOLUTION_REFUND_ONLY: DECISION_ACTION.refundNow,
  MEX_RETURN_REFUND_RESOLUTION_REJECT: DECISION_ACTION.rejectRequest,
}

/**
 * Per-item status each outcome resolves to once `Confirm` is submitted.
 * Approval lands on `returnRequested` — the buyer still has to ship — matching the
 * timeline's `Pending return` step; `deliveringReturnedItem` is the later transition
 * once the return is physically in transit.
 */
export const DECISION_ACTION_TO_STATUS: Record<DecisionAction, ReturnStatus> = {
  [DECISION_ACTION.approveReturn]: RETURN_STATUS.returnRequested,
  [DECISION_ACTION.refundNow]: RETURN_STATUS.refunded,
  [DECISION_ACTION.rejectRequest]: RETURN_STATUS.rejected,
}

/**
 * The backend's resolution enum — what `eligibleResolutions` carries and what the
 * decision submit accepts. `requestInfo` is not a selectable `DECISION_ACTION`: the
 * chat flow (BP-54655) submits it separately through the review-decision endpoint —
 * a single-item batch — before opening the room the server returns.
 */
export const RETURN_REVIEW_RESOLUTION = {
  approveReturnAndRefund:
    "MEX_RETURN_REFUND_RESOLUTION_APPROVE_RETURN_AND_REFUND",
  refundOnly: "MEX_RETURN_REFUND_RESOLUTION_REFUND_ONLY",
  reject: "MEX_RETURN_REFUND_RESOLUTION_REJECT",
  requestInfo: "MEX_RETURN_REFUND_RESOLUTION_REQUEST_INFO",
} as const

export type ReturnReviewResolution = typeof RETURN_REVIEW_RESOLUTION[keyof typeof RETURN_REVIEW_RESOLUTION]

/** What each review-screen action means on the wire. */
export const DECISION_ACTION_TO_RESOLUTION: Record<
  DecisionAction,
  ReturnReviewResolution
> = {
  [DECISION_ACTION.approveReturn]:
    RETURN_REVIEW_RESOLUTION.approveReturnAndRefund,
  [DECISION_ACTION.refundNow]: RETURN_REVIEW_RESOLUTION.refundOnly,
  [DECISION_ACTION.rejectRequest]: RETURN_REVIEW_RESOLUTION.reject,
}

/**
 * The media-upload endpoint's enums and limits.
 *
 * `mediaType` is a protobuf enum decoded as a NUMBER on the wire (proto-v2 enums
 * have no string unmarshalling) — sending the doc's `RETURN_REFUND_MEDIA_TYPE_PHOTO`
 * string 400s with "failed to decode request".
 */
export const RETURN_MEDIA_TYPE = {
  photo: 1,
} as const

export const RETURN_MEDIA_SOURCE = {
  /** The app's camera, reached through the webview's Add-photos sheet. */
  camera: "RETURN_REFUND_MEDIA_SOURCE_CAMERA",
  /** A web file picker, and the app's gallery — neither is the camera. */
  deviceLibrary: "RETURN_REFUND_MEDIA_SOURCE_DEVICE_LIBRARY",
} as const

/**
 * Which media source each row of the native Add-photos sheet reports.
 *
 * Keyed by the bridge's own `pickImage` source, so the value the merchant chose
 * rides all the way to the upload rather than being flattened to one default.
 * `gallery` maps to the device library — the app's gallery picker reads the same
 * photo roll a web file picker would.
 */
export const RETURN_MEDIA_SOURCE_BY_PICKER: Record<
  "camera" | "gallery",
  string
> = {
  camera: RETURN_MEDIA_SOURCE.camera,
  gallery: RETURN_MEDIA_SOURCE.deviceLibrary,
}

/** The server 409s an upload whose purpose does not match the item's stage. */
export const RETURN_MEDIA_UPLOAD_PURPOSE = {
  review: "REVIEW",
  inspection: "INSPECTION",
} as const

/**
 * What a media call does to the item's draft. The server infers `ADD`/`REPLACE`
 * from an absent operation, but sending it explicitly keeps the intent on the
 * wire — and the pairing rules are strict either way:
 *
 * - `ADD`    — carries `media`, and `mediaAssetIDToReplace` MUST be empty.
 * - `REPLACE`— carries `media` AND the asset id it swaps.
 * - `REMOVE` — carries the asset id ONLY; sending `media` is rejected.
 */
export const RETURN_MEDIA_OPERATION = {
  add: "ADD",
  replace: "REPLACE",
  remove: "REMOVE",
} as const

/** The upload endpoint's own hard limit — a larger photo is rejected server-side. */
export const RETURN_MEDIA_MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024

/**
 * Evidence photos the merchant may stage per item, shown as `(n/max)` on the add
 * tile and enforced by hiding it at the cap.
 *
 * FE-owned: unlike menu's server-supplied `multiPicsSetting.maxSize`, the R&R
 * contract carries no photo cap, so this constant is the only place the number
 * lives. The endpoint enforces its OWN per-item limit as well, so a value above
 * the server's turns the gate into a deferred error instead of a stop — keep
 * them aligned when BE settles its number.
 */
export const RETURN_MEDIA_MAX_PHOTOS_PER_ITEM = 4

/**
 * Inspection (decision 2) outcomes — the wire values the inspection submit accepts.
 * `reportIssue` auto-escalates to Grab support review server-side; the merchant's
 * "Reject request" wording describes the intent, not the resulting state.
 */
export const INSPECTION_OUTCOME = {
  approveRefund: "APPROVE",
  reportIssue: "REPORT_ISSUE",
} as const

export type InspectionOutcome = typeof INSPECTION_OUTCOME[keyof typeof INSPECTION_OUTCOME]

/**
 * Inspection's rejection reasons — judging the GOODS that came back, so a set
 * deliberately disjoint from decision 1's `REJECTION_REASON` (which judges the
 * buyer's claim). The submit's `issueReason` is free text server-side (≤250 chars);
 * these codes are what the FE sends.
 */
export const INSPECTION_REJECTION_REASON = {
  itemNotReturned: "ITEM_NOT_RETURNED",
  itemWorn: "ITEM_WORN",
  itemDamaged: "ITEM_DAMAGED",
  itemMissingParts: "ITEM_MISSING_PARTS",
} as const

export type InspectionRejectionReason = typeof INSPECTION_REJECTION_REASON[keyof typeof INSPECTION_REJECTION_REASON]

export const getInspectionOutcomeLabel = (
  text: Record<string, string>,
): Record<InspectionOutcome, string> => ({
  [INSPECTION_OUTCOME.approveRefund]: text.actionApproveRefund,
  [INSPECTION_OUTCOME.reportIssue]: text.actionRejectRequest,
})

export const getInspectionRejectionReasonLabel = (
  text: Record<string, string>,
): Record<InspectionRejectionReason, string> => ({
  [INSPECTION_REJECTION_REASON.itemNotReturned]:
    text.inspectionReasonItemNotReturned,
  [INSPECTION_REJECTION_REASON.itemWorn]: text.inspectionReasonItemWorn,
  [INSPECTION_REJECTION_REASON.itemDamaged]: text.inspectionReasonItemDamaged,
  [INSPECTION_REJECTION_REASON.itemMissingParts]:
    text.inspectionReasonItemMissingParts,
})

/** A merchant's pending inspection choice for one item, before `Confirm` submits. */
export interface InspectionDecision {
  outcome: InspectionOutcome
  /** Required when the outcome reports an issue. */
  reason?: InspectionRejectionReason
  /**
   * In-flight uploads for the reported issue. Same upload-on-add handling as
   * `ReviewDecision.supportingPhotos` — the staged record lives in the item's
   * `draftPhotos`. The design asks for photos OR VIDEOS here — the upload
   * endpoint accepts photos only, so video stays a raised gap rather than a
   * silent one.
   */
  supportingPhotos?: UploadFile[]
  /** Same optimistic removal as `ReviewDecision.removedDraftAssetIDs`. */
  removedDraftAssetIDs?: string[]
}

/**
 * Required whenever the chosen action is `rejectRequest`. These are decision 1's
 * reasons — judging the buyer's claim and evidence; inspection's disjoint set
 * (judging the goods that came back) is `INSPECTION_REJECTION_REASON`. Values
 * mirror the backend's `mexReview` rejection reason codes verbatim — the submit
 * sends them as `rejectionReason`, so a drifted value is a refused decision.
 */
export const REJECTION_REASON = {
  perfectConditionAtDispatch: "ITEM_IN_PERFECT_CONDITION_AT_DISPATCH",
  inconclusiveEvidence: "INCONCLUSIVE_OR_MISSING_PHOTO_EVIDENCE",
  mismatchedEvidence: "MISMATCH_IN_SUBMITTED_PHOTO_EVIDENCE",
  contradictsDescription: "CUSTOMER_CLAIM_CONTRADICTS_PRODUCT_DESCRIPTION",
  logisticsIssue: "LOGISTICS_OR_DELIVERY_ISSUE",
} as const

export type RejectionReason = typeof REJECTION_REASON[keyof typeof REJECTION_REASON]

export const getRejectionReasonLabel = (
  text: Record<string, string>,
): Record<RejectionReason, string> => ({
  [REJECTION_REASON.perfectConditionAtDispatch]:
    text.reasonPerfectConditionAtDispatch,
  [REJECTION_REASON.inconclusiveEvidence]: text.reasonInconclusiveEvidence,
  [REJECTION_REASON.mismatchedEvidence]: text.reasonMismatchedEvidence,
  [REJECTION_REASON.contradictsDescription]: text.reasonContradictsDescription,
  [REJECTION_REASON.logisticsIssue]: text.reasonLogisticsIssue,
})

/**
 * Sub-label under a reason in the dropdown. All of decision 1's reasons carry
 * helper text in the design today; Partial only so a future reason without one
 * renders label-only instead of forcing filler copy. (Inspection's reasons are
 * a different type entirely — `InspectionRejectionReason` — and could never
 * key this map.)
 */
export const getRejectionReasonHelper = (
  text: Record<string, string>,
): Partial<Record<RejectionReason, string>> => ({
  [REJECTION_REASON.perfectConditionAtDispatch]:
    text.reasonHelperPerfectConditionAtDispatch,
  [REJECTION_REASON.inconclusiveEvidence]:
    text.reasonHelperInconclusiveEvidence,
  [REJECTION_REASON.mismatchedEvidence]: text.reasonHelperMismatchedEvidence,
  [REJECTION_REASON.contradictsDescription]:
    text.reasonHelperContradictsDescription,
  [REJECTION_REASON.logisticsIssue]: text.reasonHelperLogisticsIssue,
})

/** A merchant's pending choice for one item, before `Confirm` submits the set. */
export interface ReviewDecision {
  action: DecisionAction
  /** Required when the action is `rejectRequest`. */
  reason?: RejectionReason
  /**
   * Photos still moving through the upload — each one POSTs the moment it is
   * added and drops off this list once the refetched detail echoes it back in
   * `draftPhotos`, which is the durable record. Optional — no asterisk in the
   * design.
   *
   * Holds Duxton's own `UploadFile` entries rather than a projection of them: the
   * upload list caches each thumbnail by mutating its file object, so re-wrapping
   * these would drop the preview and re-decode every image on each render.
   */
  supportingPhotos?: UploadFile[]
  /**
   * Server-staged photos hidden while their `operation: REMOVE` call and the
   * detail refetch settle — an OPTIMISTIC hide, not a cosmetic one: a server
   * that refuses the delete puts the tile back, because that draft would
   * otherwise still attach to the rejection.
   */
  removedDraftAssetIDs?: string[]
}

/**
 * How far each status has progressed, for the least-progressed-item header rule.
 * An exhaustive Record on purpose — like the bucket and label maps, a new BE enum
 * member must break the build here rather than silently fall out of the ordering.
 */
export const RETURN_STATUS_PROGRESSION: Record<ReturnStatus, number> = {
  [RETURN_STATUS.acknowledgingRequest]: 0,
  [RETURN_STATUS.returnRequested]: 1,
  [RETURN_STATUS.deliveringReturnedItem]: 2,
  [RETURN_STATUS.verifyingReturnedItem]: 3,
  [RETURN_STATUS.rejected]: 4,
  [RETURN_STATUS.requestCanceled]: 5,
  [RETURN_STATUS.refunded]: 6,
}

/** Everything the return request drawer renders. */
export interface ReturnRequestDetail {
  /** Identity of the request — matches `ReturnRequestRow.requestID`. */
  requestID: string
  /**
   * The REAL order ID — what the native hand-offs (`orderId` payloads) key on, so it
   * must never be swapped for a display value. Mock data still carries short numbers
   * here, which is fine: the header prefers `orderNumber` and mocks never reach an app.
   */
  orderID: string
  /** Short order number for the header, e.g. `GS-125`. Falls back to `orderID`. */
  orderNumber?: string
  /** Header status — reflects the least-progressed item on a mixed-state request. */
  status: ReturnStatus
  /**
   * Server-rendered display copy for the header status (`requestStatusContent`). Wins
   * over the FE `RETURN_STATUS_LABEL` lookup when present.
   */
  statusLabel?: string
  /** Pre-formatted request timestamp, e.g. `13 Sep, Tue, 12:17 PM`. */
  requestedAt: string
  /** Server copy for the items section heading; wins over the FE fallback. */
  itemsHeading?: string
  items: ReturnRequestItem[]
  /**
   * Pre-formatted request total, e.g. `S$50.00`. Optional because the contract's
   * `totalValueDisplay` may arrive unpopulated — the row is omitted rather than
   * rendering a blank amount.
   */
  total?: string
  /** Server copy for the timeline section heading; wins over the FE fallback. */
  timelineTitle?: string
  timeline: ReturnTimelineEntry[]
  /**
   * Minutes left to act. Drives the footer countdown when any item still needs action.
   * API-backed details derive it from the timeline action's `dueAt` (epoch seconds);
   * mock details carry a frozen snapshot.
   */
  slaMinutesLeft?: number
  /** Delivery problem banner, shown between the title and the item list. */
  alert?: ReturnRequestAlert
}
